import React from 'react';
import ClassNames from 'classnames';
import {
    find,
    isEqual,
    isFunction,
} from 'lodash';
import { appReduxStore } from '../../global/configureReduxStore.js';
import { i18n } from '../../global/constants.js';
import FeatureConstants from '../../global/featureConstants';
import {
    PATH_PEOPLE_SEARCH_OR_ADD,
    PEOPLE_SETTINGS_PATH,
    TITLE_PEOPLE_SEARCH_OR_ADD,
} from '../../people/global/constants.js';
import { APP_HEADER_SET_APP_HEADER_TITLE } from '../appHeader/appHeaderTypes.js';
import {
    NAVIGATION_SET_BREADCRUMBS,
    NAVIGATION_SET_HEADER_BREADCRUMBS,
    NAVIGATION_SET_SECTIONAL_TABS,
} from './navigationTypes.js';

export const getUserCollapsedStateSessionKey = () => {
    const store = appReduxStore.getState();

    const {
        bootstrap: {
            securityContext: {
                userIdentity,
            },
        },
    } = store;

    return `DESKTOP_COLLAPSED_STATE_${userIdentity.getPersonId()}`;
};

export const isCollapsedStateSessionKeySet = () => {
    const storageKeys = Object.keys(localStorage);
    const userCollapsedStateSessionKey = getUserCollapsedStateSessionKey();
    return storageKeys.includes(userCollapsedStateSessionKey);
};

export const removeCollapsedStateSessionKey = () => {
    const userCollapsedStateSessionKey = getUserCollapsedStateSessionKey();
    const isUserCollapsedSessionStateKeySet = isCollapsedStateSessionKeySet();

    if (isUserCollapsedSessionStateKeySet && userCollapsedStateSessionKey) {
        localStorage.removeItem(userCollapsedStateSessionKey);
    }
};

export const PATHS_WITH_HEADER_BREADCRUMBS = [
    '/content-management/',
    '/content-management//digital-program/',
    '/content-management//digital-program-rock/',
    '/people/search-or-add/record',
];

export const resetNavigationItems = (withTitle, title) => {
    const shouldResetTitle = withTitle && title;

    const resetActions = [
        ...(shouldResetTitle ? [{
            type: APP_HEADER_SET_APP_HEADER_TITLE,
            appHeaderTitle: title,
        }] : []),
        {
            type: NAVIGATION_SET_HEADER_BREADCRUMBS,
            navigationHeaderBreadcrumbs: [],
        },
        {
            type: NAVIGATION_SET_BREADCRUMBS,
            navigationBreadcrumbs: [],
        },
        {
            type: NAVIGATION_SET_SECTIONAL_TABS,
            sectionalTabsItems: [],
        },
    ];

    appReduxStore.dispatch(resetActions);
};

export const items = [
    {
        id: 1,
        isBeta: false,
        label: 'Home',
        segment: 'my-stuff',
        to: null,
        secondLevelItems: [
            {
                accessRequires: { permission: 'bypass' },
                isBeta: false,
                label: 'Home',
                to: '/',
                total: null,
                parentSegment: 'my-stuff',
            },
        ],
    },
    {
        id: 2,
        isBeta: false,
        label: 'People',
        segment: 'people',
        to: null,
        secondLevelItems: [
            {
                isBeta: false,
                label: TITLE_PEOPLE_SEARCH_OR_ADD,
                accessRequires: { permission: 'accessPersonSearch' },
                to: PATH_PEOPLE_SEARCH_OR_ADD,
                total: null,
                parentSegment: 'people',
                relatedPaths: [
                    `${PATH_PEOPLE_SEARCH_OR_ADD}/record/`,
                ],
            },
            {
                isBeta: false,
                label: 'Record Management',
                accessRequires: { permission: 'accessPersonAuditLogs' },
                to: '/people/audit-log',
                total: null,
                parentSegment: 'people',
                tabs: [
                    {
                        isBeta: false,
                        label: 'Audit Log',
                        accessRequires: { permission: 'accessPersonAuditLogs' },
                        to: '/people/audit-log',
                        total: null,
                    },
                ],
            }, {
                isBeta: false,
                label: 'Settings and Preferences',
                accessRequires: {
                    permission: 'manageSecurityRoles',
                },
                to: `${PEOPLE_SETTINGS_PATH}/note-categories`,
                total: null,
                parentSegment: 'people',
                relatedPaths: [
                    `${PEOPLE_SETTINGS_PATH}/note-categories`,
                ],
                tabs: [
                    {
                        isBeta: false,
                        label: 'Note Categories',
                        accessRequires: { permission: 'manageSecurityRoles' },
                        to: `${PEOPLE_SETTINGS_PATH}/note-categories`,
                        total: null,
                    },
                ],
            },
        ],
    },
    {
        id: 6,
        isBeta: false,
        label: 'Mobile Engagement',
        segment: 'content-management',
        accessRequires: {
            anyPermissions: true,
            permission: [
                'manageCampusServiceTemplate',
                'manageServiceCompanionBuilder',
                'readCampusServiceTemplate',
            ],
            feature: [FeatureConstants.ContentManagement],
        },
        to: null,
        secondLevelItems: [
            {
                isBeta: true,
                label: 'Digital Programs - Rock',
                accessRequires: {
                    anyPermissions: true,
                    permission: [
                        'manageCampusServiceTemplate',
                        'manageServiceCompanionBuilder',
                        'readCampusServiceTemplate',
                    ],
                    feature: [FeatureConstants.RockAPIforHC],
                },
                to: '/content-management/digital-program-rock',
                total: null,
                parentSegment: 'content-management',
                relatedPaths: [
                    '/content-management/digital-program-rock/',
                ],
            },
            {
                isBeta: false,
                label: 'Service Companion',
                accessRequires: {
                    anyPermissions: true,
                    permission: [
                        'manageCampusServiceTemplate',
                        'manageServiceCompanionBuilder',
                        'readCampusServiceTemplate',
                    ],
                    // feature: [FeatureConstants.ContentManagement],
                },
                to: '/content-management/service-companion',
                total: null,
                parentSegment: 'content-management',
                relatedPaths: [
                    '/content-management/service-companion/',
                ],
            },
            {
                isBeta: false,
                label: 'Digital Programs',
                accessRequires: {
                    anyPermissions: true,
                    permission: [
                        'manageCampusServiceTemplate',
                        'manageServiceCompanionBuilder',
                        'readCampusServiceTemplate',
                    ],
                    feature: [FeatureConstants.DigitalProgramManagement],
                },
                to: '/content-management/digital-program',
                total: null,
                parentSegment: 'content-management',
                relatedPaths: [
                    '/content-management/digital-program/',
                ],
            },
            {
                isBeta: false,
                label: 'Service Series Builder',
                accessRequires: {
                    anyPermissions: true,
                    permission: [
                        'manageCampusServiceTemplate',
                        'manageServiceCompanionBuilder',
                        'readCampusServiceTemplate',
                    ],
                    feature: [FeatureConstants.ContentManagement],
                },
                to: '/content-management/service-series-builder',
                total: null,
                parentSegment: 'content-management',
            },
        ],
    },
    {
        id: 11,
        isBeta: false,
        label: 'System',
        segment: 'system',
        to: null,
        secondLevelItems: [
            {
                isBeta: false,
                label: 'Platform Settings',
                accessRequires: { permission: 'manageGlobalApplicationSettings' },
                to: '/system/platform-settings/general',
                total: null,
                parentSegment: 'system',
                tabs: [
                    {
                        isBeta: false,
                        label: 'General',
                        accessRequires: { permission: 'manageGlobalApplicationSettings' },
                        to: '/system/platform-settings/general',
                        total: null,
                    },
                    {
                        isBeta: false,
                        label: 'Data Covenant',
                        accessRequires: { permission: 'manageGlobalApplicationSettings' },
                        to: '/system/platform-settings/data-covenant',
                        total: null,
                    },
                ],
            },
            {
                isBeta: false,
                label: 'Campus Settings',
                accessRequires: { permission: 'manageChurchLocations' },
                to: '/system/campus-settings',
                total: null,
                parentSegment: 'system',
            },
            {
                isBeta: false,
                label: 'Users & Teams',
                accessRequires: { permission: 'manageUserAccess' },
                to: null,
                total: null,
                parentSegment: 'system',
                relatedPaths: [
                    '/system/users/view/',
                ],
                tabs: [
                    {
                        isBeta: false,
                        label: 'Users',
                        accessRequires: { permission: 'manageUserAccess' },
                        to: '/system/users',
                        total: null,
                    },
                    {
                        isBeta: false,
                        label: 'Teams',
                        accessRequires: { permission: 'manageTeams' },
                        to: '/system/teams',
                        total: null,
                    },
                ],
            },
            {
                isBeta: false,
                label: 'Security & Permissions',
                accessRequires: { permission: 'manageSecurityRoles' },
                to: null,
                total: null,
                parentSegment: 'system',
                relatedPaths: [
                    '/system/security-roles/view/',
                    '/system/security-roles/edit',
                ],
                tabs: [
                    {
                        isBeta: false,
                        label: 'Security Roles',
                        accessRequires: { permission: 'manageSecurityRoles' },
                        to: '/system/security-roles',
                        total: null,
                    },
                    {
                        isBeta: false,
                        label: 'Permissions',
                        accessRequires: { permission: 'manageSecurityRoles' },
                        to: '/system/permissions',
                        total: null,
                    },
                ],
            },
        ],
    },
];

export const parseTabsToSectionalTabsItems = (tabs = [], routerPushFn) => (
    tabs.map((item, index) => ({
        accessRequires: item.accessRequires,
        key: `navigation_tab--key-item-${index}`,
        title: item.label,
        onClick: () => routerPushFn(item.to),
        to: item.to,
    }))
);

export const areSectionalTabsEqual = (currentSectinonalTabs, nextSectionalTabs) => {
    const currentSectionalPaths = currentSectinonalTabs.map((item) => item.to);
    const nextSectionalPaths = nextSectionalTabs.map((item) => item.to);

    return isEqual(currentSectionalPaths, nextSectionalPaths);
};

const simplifyNavigationBreadcrumbs = (navigationBreadcrumbs = []) => (
    navigationBreadcrumbs.map((navigationBreadcrumb) => ({
        title: navigationBreadcrumb.title,
        items: navigationBreadcrumb.items,
    }))
);

export const areNavigationBreadcrumbsEqual = (
    currentNavigationBreadcrumbs,
    nextNavigationBreadcrumbs,
) => (isEqual(
    simplifyNavigationBreadcrumbs(currentNavigationBreadcrumbs),
    simplifyNavigationBreadcrumbs(nextNavigationBreadcrumbs),
));

const simplifyNavigationHeaderBreadcrumbs = (navigationHeaderBreadcrumbs = []) => (
    navigationHeaderBreadcrumbs.map(({ title, to }) => ({ title,
        to }))
);

export const areNavigationHeaderBreadcrumbsEqual = (
    currentNavigationHeaderBreadcrumbs,
    nextNavigationHeaderBreadcrumbs,
) => (
    isEqual(
        simplifyNavigationHeaderBreadcrumbs(currentNavigationHeaderBreadcrumbs),
        simplifyNavigationHeaderBreadcrumbs(nextNavigationHeaderBreadcrumbs),
    )
);

export const updateNavigationItems = (
    nextSectionalTabs,
    nextNavigationHeaderBreadcrumbs,
    nextNavigationBreadcrumbs,
    sectionalTabsOnBeforeChange,
    title,
) => {
    const store = appReduxStore.getState();

    const {
        navigation: {
            navigationBreadcrumbs,
            navigationHeaderBreadcrumbs,
            sectionalTabsItems,
        },
    } = store;

    const shouldUpdateSectionalTabs = !areSectionalTabsEqual(
        sectionalTabsItems, nextSectionalTabs,
    );

    const shouldUpdateHeaderBreadcrumbs = !areNavigationHeaderBreadcrumbsEqual(
        navigationHeaderBreadcrumbs,
        nextNavigationHeaderBreadcrumbs,
    );

    const shouldUpdateNavigationBreadcrumbs = !areNavigationBreadcrumbsEqual(
        navigationBreadcrumbs,
        nextNavigationBreadcrumbs,
    );

    const actionsToDispatch = [
        ...(shouldUpdateSectionalTabs ? [{
            type: NAVIGATION_SET_SECTIONAL_TABS,
            sectionalTabsItems: nextSectionalTabs,
            ...(isFunction(sectionalTabsOnBeforeChange) && { sectionalTabsOnBeforeChange }),
        }] : []),
        ...(shouldUpdateHeaderBreadcrumbs ? [{
            type: NAVIGATION_SET_HEADER_BREADCRUMBS,
            navigationHeaderBreadcrumbs: nextNavigationHeaderBreadcrumbs,
        }] : []),
        ...(shouldUpdateNavigationBreadcrumbs ? [{
            type: NAVIGATION_SET_BREADCRUMBS,
            navigationBreadcrumbs: nextNavigationBreadcrumbs,
        }] : []),
        ...(title ? [{
            type: APP_HEADER_SET_APP_HEADER_TITLE,
            appHeaderTitle: title,
        }] : []),
    ];

    appReduxStore.dispatch(actionsToDispatch);
};

export const getLevelTwoBreadcrumbs = (navigationItemsV3, currentLevelTwoItem) => {
    let levelTwoBreadcrumbs = [];

    if (navigationItemsV3 && currentLevelTwoItem) {
        const {
            label,
            tabs,
            parentSegment,
        } = currentLevelTwoItem;

        if (!tabs) {
            return [];
        }

        const parentItem = find(navigationItemsV3, { segment: parentSegment });

        levelTwoBreadcrumbs = [
            { title: parentItem?.label || '',
                items: [],
                parentItem: {} },
            { title: label,
                items: tabs || [],
                parentItem },
        ];
    }

    return levelTwoBreadcrumbs;
};

export const svg = (type, isMobile) => {
    const containerClasses = ClassNames('section-icon', type);

    switch (type) {
        case 'data-capture':
            return (
                <svg
                    className={containerClasses}
                    version="1"
                    viewBox="0 0 26 26"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('Data Capture')}</title>
                    <defs>
                        <path
                            d="M13.5700651,15.8460946 L10.1540512,12.4300807 L17.9308345,4.65858429 L19.8335778,2.75338543 L23.249793,6.16960069 L21.3474454,8.0751707 L13.5700651,15.8460946 Z M11.8120902,17.6026101 L10.7701665,18.6436689 L6.5,19.5 L7.35435178,15.2278768 L8.39620849,14.1867284 L11.8120902,17.6026101 Z M24.854931,4.56174381 L21.4390392,1.14585204 L21.8777328,0.706592331 C22.8196695,-0.235530777 24.3524069,-0.235530777 25.2935474,0.706592331 C26.2354842,1.64792107 26.2354842,3.18026127 25.2935474,4.12238438 L24.854931,4.56174381 Z M1.625,22.75 L24.375,22.75 C25.2724627,22.75 26,23.4775373 26,24.375 C26,25.2724627 25.2724627,26 24.375,26 L1.625,26 C0.727537282,26 1.09907485e-16,25.2724627 0,24.375 C-1.09907485e-16,23.4775373 0.727537282,22.75 1.625,22.75 Z"
                            id="data-capture-a"
                        />
                    </defs>
                    <g
                        fill="none"
                        fillRule="evenodd"
                    >
                        <mask
                            fill="#fff"
                            id="data-capture-b"
                        >
                            <use xlinkHref="#data-capture-a" />
                        </mask>
                        <use
                            fill="#1C2530"
                            xlinkHref="#data-capture-a"
                        />
                        <g
                            fill="#DBE0E3"
                            mask="url(#data-capture-b)"
                        >
                            <path d="M0 0H26V26H0z" />
                        </g>
                    </g>
                </svg>
            );
        case 'collapse-menu':
            return isMobile ? (
                <svg
                    className={containerClasses}
                    version="1"
                    viewBox="0 0 26 26"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('Menu')}</title>
                    <defs>
                        <path
                            d="M0,1.0026123 C0,0.448884819 0.451278687,2.60990041e-16 1.00329963,4.20714176e-16 L24.9967004,7.36306877e-15 C25.5508075,7.52339651e-15 26,0.443349075 26,1.0026123 L26,4.1973877 C26,4.75111518 25.5487213,5.2 24.9967004,5.2 L1.00329963,5.2 C0.449192547,5.2 0,4.75665092 0,4.1973877 L0,1.0026123 Z M0,11.4026123 C0,10.8488848 0.451278687,10.4 1.00329963,10.4 L24.9967004,10.4 C25.5508075,10.4 26,10.8433491 26,11.4026123 L26,14.5973877 C26,15.1511152 25.5487213,15.6 24.9967004,15.6 L1.00329963,15.6 C0.449192547,15.6 0,15.1566509 0,14.5973877 L0,11.4026123 Z M0,21.8026123 C0,21.2488848 0.451278687,20.8 1.00329963,20.8 L24.9967004,20.8 C25.5508075,20.8 26,21.2433491 26,21.8026123 L26,24.9973877 C26,25.5511152 25.5487213,26 24.9967004,26 L1.00329963,26 C0.449192547,26 0,25.5566509 0,24.9973877 L0,21.8026123 Z"
                            id="menu-a"
                        />
                    </defs>
                    <g
                        fill="none"
                        fillRule="evenodd"
                    >
                        <mask
                            fill="#fff"
                            id="menu-b"
                        >
                            <use xlinkHref="#menu-a" />
                        </mask>
                        <use
                            fill="#FFF"
                            transform="matrix(-1 0 0 1 26 0)"
                            xlinkHref="#menu-a"
                        />
                        <g
                            fill="#DBE0E3"
                            mask="url(#menu-b)"
                        >
                            <path d="M0 0H26V26H0z" />
                        </g>
                    </g>
                </svg>
            ) : (
                <svg
                    className={containerClasses}
                    version="1.1"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('Menu')}</title>
                    <defs>
                        <path
                            d="M8.06040048,0.195905095 L9.03069109,1.16321628 C9.29235475,1.42407647 9.29006453,1.85045513 9.02485958,2.11484573 L3.12157838,8 L9.02485958,13.8851543 C9.29006453,14.1495449 9.29235475,14.5759235 9.03069109,14.8367837 L8.06040048,15.8040949 C7.79841618,16.0652747 7.3756467,16.0672596 7.1058399,15.7982813 L0.200503224,8.91414852 C-0.0523535958,8.66206813 -0.06620871,8.26273307 0.159838543,8 C-0.06620871,7.73726693 -0.0523535958,7.33793187 0.200503224,7.08585148 L7.1058399,0.201718701 C7.3756467,-0.0672596148 7.79841618,-0.0652747378 8.06040048,0.195905095 Z M14.8350377,0.195905095 L15.8053283,1.16321628 C16.066992,1.42407647 16.0647017,1.85045513 15.7994968,2.11484573 L9.89621558,8 L15.7994968,13.8851543 C16.0647017,14.1495449 16.066992,14.5759235 15.8053283,14.8367837 L14.8350377,15.8040949 C14.5730534,16.0652747 14.1502839,16.0672596 13.8804771,15.7982813 L6.97514042,8.91414852 C6.7222836,8.66206813 6.70842849,8.26273307 6.93447574,8 C6.70842849,7.73726693 6.7222836,7.33793187 6.97514042,7.08585148 L13.8804771,0.201718701 C14.1502839,-0.0672596148 14.5730534,-0.0652747378 14.8350377,0.195905095 Z"
                            id="path-1"
                        />
                    </defs>
                    <g
                        fill="none"
                        fillRule="evenodd"
                    >
                        <g id="Expand-Icon">
                            <mask
                                fill="#fff"
                                id="menu-b"
                            >
                                <use xlinkHref="#menu-a" />
                            </mask>
                            <use
                                fill="#FFFFFF"
                                id="Combined-Shape"
                                transform="translate(8.000000, 8.000000) rotate(-180.000000) translate(-8.000000, -8.000000) "
                                xlinkHref="#path-1"
                            />
                        </g>
                    </g>
                </svg>
            );
        case 'ministries-central':
            return (
                <svg
                    height="16px"
                    version="1.1"
                    viewBox="0 0 16 16"
                    width="16px"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('Ministries Central')}</title>
                    <g
                        fill="none"
                        fillRule="evenodd"
                        id="Layout-&amp;-Behaviors"
                        stroke="none"
                        strokeWidth="1"
                    >
                        <g
                            fill="#FFFFFF"
                            id="Artboard"
                            transform="translate(-338.000000, -419.000000)"
                        >
                            <path
                                d="M348.12093,423.598602 C347.9258,425.156917 347.607011,426.543049 347.198174,427.75966 C347.917279,427.422409 348.633995,427.233285 349.172155,427.127772 L349.270925,427.108952 L349.353512,427.094053 L349.434117,427.08025 L349.512361,427.067555 L349.642881,427.047943 C354.641299,426.645471 354.935921,428.816826 352.556924,432.064078 L352.484016,432.162807 L352.410746,432.261606 C350.864594,434.317994 348.847455,435.216204 346.359329,434.956235 C345.637556,434.801033 345.096727,434.508465 344.736928,434.078157 C344.87353,433.932262 345.009859,433.79038 345.145917,433.652513 C346.575632,432.577029 348.067995,431.633802 349.622992,430.82284 L349.767915,430.747735 L349.804254,430.728246 L349.838629,430.708322 C350.323101,430.416873 350.4495,429.880602 349.728999,430.063096 L349.623079,430.084032 L349.516836,430.106287 L349.412005,430.12945 C347.681188,430.521594 345.037863,431.597795 341.206233,434.380259 L341.180239,434.384539 C342.008587,431.024433 343.177364,427.848377 344.686502,424.856359 L344.769276,424.693107 C345.152684,423.940843 344.860262,423.131926 344.164264,423.959456 L344.069731,424.060399 C343.609965,424.556553 343.109097,425.173332 342.579771,425.943602 L342.506376,426.042128 C341.953907,426.791569 341.356855,427.778337 340.7485,429.082456 C340.132668,430.307826 339.500263,431.74936 338.866649,433.447185 C338.450264,432.831038 338.300412,432.152211 338.301253,432.154518 C337.796623,430.241197 338.013986,428.441631 338.314852,426.889188 C338.836238,424.628941 340.344145,422.325666 343.517637,420.267011 C348.087627,417.473309 348.691351,419.617947 348.12093,423.598602 Z"
                                id="Nav_Ministries-Central"
                            />
                        </g>
                    </g>
                </svg>
            );
        case 'my-dashboard':
        case 'my-stuff':
            return (
                <svg
                    className={containerClasses}
                    version="1"
                    viewBox="0 0 26 26"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('My Dashboard')}</title>
                    <defs>
                        <path
                            d="M14.6466667,26 L14.6466667,21.105841 C14.6466667,20.4037711 14.0646368,19.8346309 13.3466667,19.8346309 L12.7183333,19.8346309 C12.0003632,19.8346309 11.4183333,20.4037711 11.4183333,21.105841 L11.4183333,26 L8.23333333,26 C7.51536316,26 6.93333333,25.4308598 6.93333333,24.7287899 L6.93333333,15.830319 C6.93335886,15.4310465 7.12571659,15.1406677 7.45248833,14.8157026 L12.4724383,9.94633226 L12.5141033,9.90565354 C13.0217829,9.4092266 12.9782171,9.4092266 13.4858967,9.90565354 L18.5373067,14.8082661 C18.8700705,15.1319992 19.0666421,15.4266615 19.0666667,15.830319 L19.0666667,24.7287899 C19.0666667,25.4308598 18.4846368,26 17.7666667,26 L14.6466667,26 Z M23.8625353,15.2308276 C23.6190556,15.4672041 23.2888188,15.6 22.9444777,15.6 C22.6001365,15.6 22.2698997,15.4672041 22.02642,15.2308276 L12.9828138,6.45119404 L3.97315803,15.1973652 C3.46617359,15.689385 2.64435176,15.689385 2.13736732,15.1973652 L0.380123517,13.4914745 C-0.126707839,12.9993061 -0.126707839,12.2015009 0.380123517,11.7093324 L12.0625166,0.368342275 C12.5435899,-0.0981311827 13.3140542,-0.124859342 13.8281993,0.307089018 C13.8657414,0.33608973 13.9016072,0.367078512 13.9356333,0.39991417 L25.6196493,11.7424798 C26.1266568,12.2345757 26.1268021,13.032478 25.6199738,13.5247479 L23.8625353,15.2308276 Z"
                            id="my-dashboard-a"
                        />
                    </defs>
                    <g
                        fill="none"
                        fillRule="evenodd"
                    >
                        <mask
                            fill="#fff"
                            id="my-dashboard-b"
                        >
                            <use xlinkHref="#my-dashboard-a" />
                        </mask>
                        <use
                            fill="#FFF"
                            xlinkHref="#my-dashboard-a"
                        />
                        <g
                            fill="#DBE0E3"
                            mask="url(#my-dashboard-b)"
                        >
                            <path d="M0 0H26V26H0z" />
                        </g>
                    </g>
                </svg>
            );
        case 'events-central':
            return (
                <svg
                    className={containerClasses}
                    version="1"
                    viewBox="0 0 26 26"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('Events Central')}</title>
                    <defs>
                        <path
                            d="M21,3 L21.9950534,3 C23.1023548,3 24,3.90234375 24,4.99456145 L24,22.0054385 C24,23.1070044 23.1029738,24 21.9950534,24 L2.00494659,24 C0.897645164,24 0,23.0976562 0,22.0054385 L0,4.99456145 C0,3.89299558 0.897026226,3 2.00494659,3 L3,3 L3,5.49013764 C3,6.61019951 3.89449617,7.5 4.99791312,7.5 L7.00208688,7.5 C8.10173938,7.5 9,6.60015397 9,5.49013764 L9,3 L15,3 L15,5.49013764 C15,6.61019951 15.8944962,7.5 16.9979131,7.5 L19.0020869,7.5 C20.1017394,7.5 21,6.60015397 21,5.49013764 L21,3 Z M4.5,0.990777969 C4.5,0.443586406 4.94266033,0 5.49895656,0 L6.50104344,0 C7.05275191,0 7.5,0.45097518 7.5,0.990777969 L7.5,5.00922203 C7.5,5.55641359 7.05733967,6 6.50104344,6 L5.49895656,6 C4.94724809,6 4.5,5.54902482 4.5,5.00922203 L4.5,0.990777969 Z M16.5,0.990777969 C16.5,0.443586406 16.9426603,0 17.4989566,0 L18.5010434,0 C19.0527519,0 19.5,0.45097518 19.5,0.990777969 L19.5,5.00922203 C19.5,5.55641359 19.0573397,6 18.5010434,6 L17.4989566,6 C16.9472481,6 16.5,5.54902482 16.5,5.00922203 L16.5,0.990777969 Z"
                            id="events-central-a"
                        />
                    </defs>
                    <g
                        fill="none"
                        fillRule="evenodd"
                    >
                        <mask
                            fill="#fff"
                            id="events-central-b"
                        >
                            <use xlinkHref="#events-central-b" />
                        </mask>
                        <use
                            fill="#FFF"
                            xlinkHref="#events-central-a"
                        />
                        <g
                            fill="#DBE0E3"
                            mask="url(#events-central-b)"
                        >
                            <path d="M0 0H26V26H0z" />
                        </g>
                    </g>
                </svg>
            );
        case 'people':
            return (
                <svg
                    height="16px"
                    version="1.1"
                    viewBox="0 0 16 16"
                    width="16px"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('People')}</title>
                    <g
                        fill="none"
                        fillRule="evenodd"
                        id="Layout-&amp;-Behaviors"
                        stroke="none"
                        strokeWidth="1"
                    >
                        <g
                            fill="#FFFFFF"
                            fillRule="nonzero"
                            id="Artboard"
                            transform="translate(-338.000000, -369.000000)"
                        >
                            <path
                                d="M352.1545,380.541612 C352.24667,380.209344 352.295409,379.866539 352.2995,379.521751 C352.2995,377.728997 351.2555,376.848618 348.878,376.515664 C349.296708,376.139133 349.637916,375.684535 349.8825,375.177347 C352.777,375.194845 354,375.714274 354,377.298056 C354,378.29392 353.269,379.650734 352.1545,380.541612 Z M350.0885,374.663918 C350.739146,372.697195 349.897832,370.545901 348.0855,369.54212 C349.048698,368.842948 350.3456,368.817939 351.335045,369.479456 C352.32449,370.140974 352.796871,371.34888 352.518651,372.506011 C352.240432,373.663143 351.270552,374.524369 350.0885,374.663918 L350.0885,374.663918 Z M345.999,376.097224 C344.461246,376.092668 343.217926,374.843333 343.221,373.305787 C343.224085,371.76824 344.472399,370.523894 346.010159,370.525498 C347.547918,370.527104 348.793631,371.774054 348.7935,373.311603 C348.790961,374.852325 347.539933,376.099376 345.999,376.097224 Z M341.691,373.311603 C341.690895,373.769335 341.763811,374.224153 341.907,374.658918 C340.736758,374.500873 339.786273,373.63558 339.519488,372.4854 C339.252703,371.33522 339.725239,370.139946 340.706445,369.483009 C341.687651,368.826071 342.972962,368.844429 343.935,369.529122 C342.551128,370.284407 341.690426,371.735205 341.691,373.311603 L341.691,373.311603 Z M343.107,376.504665 C340.677,376.829121 339.6005,377.7095 339.6005,379.521751 C339.603679,379.830816 339.643456,380.138416 339.719,380.438126 C338.6755,379.547748 338,378.255925 338,377.298056 C338,375.714774 339.234,375.194845 342.115,375.177347 C342.356942,375.679624 342.6938,376.130347 343.107,376.504665 L343.107,376.504665 Z M345.975,377.751494 C349.441,377.751494 350.9005,378.339914 350.9005,380.196659 C350.9005,382.053404 348.695,385 345.975,385 C343.255,385 341.0495,382.053904 341.0495,380.196659 C341.0495,378.339414 342.523,377.751494 345.975,377.751494 Z"
                                id="Nav_People"
                            />
                        </g>
                    </g>
                </svg>
            );
        case 'system':
            return (
                <svg
                    className={containerClasses}
                    version="1"
                    viewBox="0 0 26 26"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('System')}</title>
                    <defs>
                        <path
                            d="M1.625,0 L4.875,0 C5.77246272,0 6.5,0.727537282 6.5,1.625 L6.5,24.375 C6.5,25.2724627 5.77246272,26 4.875,26 L1.625,26 C0.727537282,26 0,25.2724627 0,24.375 L0,1.625 C0,0.727537282 0.727537282,0 1.625,0 Z M11.375,9.75 L24.375,9.75 C25.2724627,9.75 26,10.4775373 26,11.375 L26,24.375 C26,25.2724627 25.2724627,26 24.375,26 L11.375,26 C10.4775373,26 9.75,25.2724627 9.75,24.375 L9.75,11.375 C9.75,10.4775373 10.4775373,9.75 11.375,9.75 Z M11.375,0 L24.375,0 C25.2724627,0 26,0.727537282 26,1.625 L26,4.875 C26,5.77246272 25.2724627,6.5 24.375,6.5 L11.375,6.5 C10.4775373,6.5 9.75,5.77246272 9.75,4.875 L9.75,1.625 C9.75,0.727537282 10.4775373,0 11.375,0 Z"
                            id="system-a"
                        />
                    </defs>
                    <g
                        fill="none"
                        fillRule="evenodd"
                    >
                        <mask
                            fill="#fff"
                            id="system-b"
                        >
                            <use xlinkHref="#system-a" />
                        </mask>
                        <use
                            fill="#1C2530"
                            xlinkHref="#system-a"
                        />
                        <g
                            fill="#DBE0E3"
                            mask="url(#system-b)"
                        >
                            <path d="M0 0H26V26H0z" />
                        </g>
                    </g>
                </svg>
            );
        case 'user-settings':
            return (
                <svg
                    className={containerClasses}
                    version="1"
                    viewBox="0 0 26 26"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('User Settings')}</title>
                    <defs>
                        <path
                            d="M4.96405645,19.8552377 C5.08105261,19.192751 5.30691425,18.6082134 5.60323054,18.2835099 C5.95622439,17.8965326 9.26720702,17.2497502 9.26720702,17.2497502 C10.347196,17.0196444 10.7268457,16.0666943 10.1603803,15.0927742 C10.1603803,15.0927742 8.9375,13.5480528 8.9375,11.578125 C8.9375,8.71489917 9.5089145,6.5 13,6.5 C16.4915593,6.5 17.0625,8.71489917 17.0625,11.578125 C17.0625,13.5419587 15.8375182,15.0976308 15.8375182,15.0976308 C15.2566743,16.0507787 15.6751257,17.0112193 16.7752255,17.2532577 C16.7752255,17.2532577 20.0631808,17.9056738 20.4057507,18.2835099 C20.6994737,18.6078602 20.9225928,19.1914798 21.0377886,19.8530764 C22.6120746,18.0084113 23.5625,15.6151975 23.5625,13 C23.5625,7.16649233 18.8335077,2.4375 13,2.4375 C7.16649233,2.4375 2.4375,7.16649233 2.4375,13 C2.4375,15.616219 3.38866806,18.0102808 4.96405645,19.8552377 Z M13,26 C5.82029825,26 0,20.1797017 0,13 C0,5.82029825 5.82029825,0 13,0 C20.1797017,0 26,5.82029825 26,13 C26,20.1797017 20.1797017,26 13,26 Z"
                            id="user-settings-a"
                        />
                    </defs>
                    <g
                        fill="none"
                        fillRule="evenodd"
                    >
                        <mask
                            fill="#fff"
                            id="user-settings-b"
                        >
                            <use xlinkHref="#user-settings-a" />
                        </mask>
                        <use
                            fill="#1C2530"
                            xlinkHref="#user-settings-a"
                        />
                        <g
                            fill="#DBE0E3"
                            mask="url(#user-settings-b)"
                        >
                            <path d="M0 0H26V26H0z" />
                        </g>
                    </g>
                </svg>
            );
        case 'mobile-engagement':
            return (
                <svg
                    height="16px"
                    version="1.1"
                    viewBox="0 0 9 16"
                    width="16px"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                >
                    <title>{i18n('Content Management')}</title>
                    <g
                        fill="none"
                        fillRule="evenodd"
                        id="Layout-&amp;-Behaviors"
                        stroke="none"
                        strokeWidth="1"
                    >
                        <g
                            fill="#FFFFFF"
                            id="Artboard"
                            transform="translate(-376.000000, -305.000000)"
                        >
                            <path
                                d="M377,305 L383.5,305 C384.052285,305 384.5,305.447715 384.5,306 L384.5,320 C384.5,320.552285 384.052285,321 383.5,321 L377,321 C376.447715,321 376,320.552285 376,320 L376,306 C376,305.447715 376.447715,305 377,305 Z M378.75,306 C378.611929,306 378.5,306.111929 378.5,306.25 C378.5,306.388071 378.611929,306.5 378.75,306.5 L381.75,306.5 C381.888071,306.5 382,306.388071 382,306.25 C382,306.111929 381.888071,306 381.75,306 L378.75,306 Z M380.25,319 C379.835786,319 379.5,319.335786 379.5,319.75 C379.5,320.164214 379.835786,320.5 380.25,320.5 C380.664214,320.5 381,320.164214 381,319.75 C381,319.335786 380.664214,319 380.25,319 Z M377.5,307.5 C377.223858,307.5 377,307.723858 377,308 L377,318 C377,318.276142 377.223858,318.5 377.5,318.5 L383,318.5 C383.276142,318.5 383.5,318.276142 383.5,318 L383.5,308 C383.5,307.723858 383.276142,307.5 383,307.5 L377.5,307.5 Z"
                                id="Nav_Mobile-Engagement"
                            />
                        </g>
                    </g>
                </svg>
            );
        case 'journey':
            return (
                <svg
                    height="16px"
                    viewBox="0 0 24 24"
                    width="16px"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <title>{i18n('Journey')}</title>
                    <g
                        className="cls-2"
                        fill="#FFFFFF"
                    >
                        <path
                            className="cls-3"
                            d="M22.9,12.87a6.69,6.69,0,0,0-5.41-2.36h-11C4.88,10.51,3,9.62,3,7.75S4.83,5,6.35,5h2.5a3.5,3.5,0,1,0,0-3H6.51C4.71,2,0,2.68,0,7.75c0,2.9,1.46,5.76,6.51,5.76h11c2.47,0,3.5,1.3,3.5,2.74a2.54,2.54,0,0,1-2.78,2.67L15.14,19a3.5,3.5,0,1,0,0,3h2.34C21.9,22,24,19.5,24,16.25a5.39,5.39,0,0,0-1.1-3.38"
                            id="Journey"
                            transform="translate(0 0)"
                        />
                    </g>
                </svg>
            );
        case 'giving-central':
            return (
                <svg
                    className={containerClasses}
                    viewBox="0 0 26 22"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <title>{i18n('Giving Central')}</title>
                    <g
                        className="cls-2"
                        fill="#FFFFFF"
                    >
                        <path
                            className="cls-3"
                            d="M23.12245,11.0561933 C23.12245,11.0561933 16.4074197,15.8210357 14.308671,17.2710795 C12.2099223,18.7211232 11.0189141,17.5599014 11.0189141,17.5599014 L7.46327303,15.1088377 L4.2148904,17.3962516 C3.45948237,17.9281873 2.5,17 2.14178288,16.4352691 L0.213455765,13.9054686 C-0.27192466,12.5827541 0.0984638193,10.95814 1.04074159,10.2767913 C3.89940689,8.26639694 5.70500359,6.99928672 6.47680285,6.46171812 C7.85504659,5.50175278 8.70759771,6.33651742 8.70759771,6.33651742 L16.1911875,9.75452209 C16.1911875,9.75452209 17.099147,11.0186823 16.6067118,12.3714296 C16.1142767,13.724177 14.8248846,13.3061846 14.8248846,13.3061846 L11.7527145,11.9132648 L11.5757154,12.714098 L14.8242475,14.1763594 C14.8242475,14.1763594 16.234265,14.5567096 16.9913628,13.0238999 C17.4380856,12.1688572 17.5007736,11.0318448 17.1536459,10.0804685 L21.8574948,6.86569011 C21.8574948,6.86569011 23.281813,6.31230552 23.854638,8.02036334 C24.4274629,9.72842116 23.12245,11.0561933 23.12245,11.0561933 Z"
                            id="GivingCentral"
                            transform="translate(0 0)"
                        />
                    </g>
                </svg>
            );
        default:
            return null;
    }
};
